import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { SquarePenIcon } from 'lucide-react';
import { useIntl } from 'react-intl';

import { AddNewStorySectionButton } from '@/components/add-new-story-section-button';
import { StorySettingsDialog } from '@/components/story-settings-dialog';
import { apiPutStoryById, APIPutStoryPayload, Story } from '@/shared/api';
import { useDebouncedMutation } from '@/shared/hooks/use-debounced-mutation';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import { INPUT_DEBOUNCE_TIME } from '@/shared/utils';

import { EditableTitle } from './editable-title';
import { StorySectionItem } from './story-section-item';
import { StoryShareLinkDialog } from './story-share-link-dialog';

export const StoryAuthorPage = ({ storyId }: { storyId: string }) => {
  const intl = useIntl();
  const query = useQuery(storyByIdQueryOptions(storyId));
  const queryClient = useQueryClient();

  const { handleDebouncedMutation } = useDebouncedMutation<
    AxiosResponse<Story>,
    { id: string; payload: APIPutStoryPayload }
  >({
    mutationFn: (data) => apiPutStoryById(data.id, data.payload),
    onSuccess: (updatedStory) => {
      queryClient.setQueryData(storyByIdQueryOptions(storyId).queryKey, updatedStory);
    },
    debounceTime: INPUT_DEBOUNCE_TIME,
  });

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleDebouncedMutation({ id: storyId, payload: { name: event.target.value } });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={() => {}}>
      <div className="relative pb-20">
        <div className="flex flex-col justify-between gap-y-4 bg-white px-4 pb-6 md:flex-row md:px-10">
          <div>
            <div className="flex gap-2 text-2xl">
              <span>{intl.formatMessage({ id: 'story.title' })}</span>

              {query.data && (
                <EditableTitle
                  initialTitle={query.data.data.name}
                  trigger={<SquarePenIcon className="h-4 w-4 cursor-pointer" />}
                  onChange={handleChangeName}
                  placeholder={intl.formatMessage({ id: 'input.story.placeholder' })}
                  className="min-w-10 text-2xl"
                >
                  <b>
                    {query.data.data.name
                      ? intl.formatMessage({
                          id: `storyTemplate.${query.data.data.name}`,
                          defaultMessage: query.data.data.name,
                        })
                      : intl.formatMessage({ id: 'story.newStory' })}
                  </b>
                </EditableTitle>
              )}
            </div>

            {query.data?.data.template && (
              <p className="text-muted-foreground">
                {query.data.data.template.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {query.data && (
              <StorySettingsDialog
                metadata={query.data.data.metadata}
                storyId={storyId}
              />
            )}

            {query.data && (
              <StoryShareLinkDialog storyId={storyId} metadata={query.data.data.metadata}>
                <Button>{intl.formatMessage({ id: 'shareLink' })}</Button>
              </StoryShareLinkDialog>
            )}
          </div>
        </div>

        <div className="px-4 md:px-10">
          <div className="flex flex-col gap-8">
            {query.data?.data.questionsCategories.map((category) => (
              <StorySectionItem {...category} key={category.id} />
            ))}
          </div>

          <div className="mt-8 flex">
            <AddNewStorySectionButton storyId={storyId} />
          </div>
        </div>
      </div>
    </DndContext>
  );
};
